import { createContext, ReactElement, useContext, useState } from 'react';

type LanguageProviderProps = {
  children?: ReactElement;
};

export const SUPPORTED_LANGUAGE = ['en', 'es'] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGE[number];

const LanguageContext = createContext<{
  language: SupportedLanguage;
  updateLanguage: (language: SupportedLanguage) => void;
} | null>(null);

const languageFromNavigator = (): SupportedLanguage => {
  const language = navigator.language;
  return isMatchSpanishLanguage(language) ? 'es' : 'en';
};

const isMatchSpanishLanguage = (language: string): boolean =>
  /^es\b/.test(language);

const currentLanguage =
  (localStorage.getItem('language') as SupportedLanguage) ??
  languageFromNavigator();

export const LanguageProvider = (props: LanguageProviderProps) => {
  const { children } = props;
  const [language, setLanguage] = useState<SupportedLanguage>(currentLanguage);
  const updateLanguage = (lang: SupportedLanguage) => {
    if (language === lang) {
      console.debug(
        `The language ${language} is already set. Ignore the function update language`,
      );
      return;
    }
    localStorage.setItem('language', lang);
    setLanguage(lang);
  };
  return (
    <LanguageContext.Provider
      value={{ language: language, updateLanguage: updateLanguage }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const languageContext = useContext(LanguageContext);
  if (languageContext == null) {
    throw Error('Language is not in the context');
  }
  return languageContext;
};
